/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/screens/MethodSelectScreen.tsx
 * PURPOSE: SC-017-EMP-ATTENDANCE [2] method_select — chọn phương thức chấm công
 *          (GPS/Timemark, FaceID khoá), config-driven theo SC-004-ATTENDANCE-CONFIG.
 *          Web trước đây KHÔNG có màn này — 3 nút gộp chung 1 card. Đây là bước
 *          đầu dựng lại theo state machine 11 màn của wireframe (mobile-anchor).
 *          2026-09-04 (structural layout parity, root causes #1/#2): mobile's
 *          renderMethodSelect returns a bare fragment (no Card wrap) and has
 *          no in-body title (AppHeader supplies it via headerConfig). Removed
 *          the `.card` wrapper + duplicate `<h2>` — AttendanceModern.tsx's
 *          page header is now state-driven and supplies "Chọn phương thức"
 *          for this screen.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE [2]
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx renderMethodSelect (:771-815)
 * - Nhãn Việt khớp mobile/src/i18n/attendanceV2.i18n.ts methodSelect.*
 *  */

import React, { useState } from 'react';
import styles from '@/pages/AttendanceModern.module.css';

export type ClockInMethod = 'gps' | 'timemark';

interface MethodSelectScreenProps {
  /** SC-004-ATTENDANCE-CONFIG — phương thức nào đang bật cho tenant/chi nhánh */
  gpsMethodEnabled: boolean;
  timemarkMethodEnabled: boolean;
  onCancel: () => void;
  onContinue: (method: ClockInMethod) => void;
}

export const MethodSelectScreen: React.FC<MethodSelectScreenProps> = ({
  gpsMethodEnabled,
  timemarkMethodEnabled,
  onCancel,
  onContinue,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<ClockInMethod>(
    gpsMethodEnabled ? 'gps' : 'timemark',
  );
  const [showFaceIdLocked, setShowFaceIdLocked] = useState(false);

  return (
    <>
      {gpsMethodEnabled && (
        <button
          type="button"
          className={`${styles.methodCard} ${selectedMethod === 'gps' ? styles.methodCardSelected : ''}`}
          onClick={() => setSelectedMethod('gps')}
          data-testid="method-card-gps"
        >
          <div className={styles.methodCardHeader}>
            <span className={styles.methodCardTitle}>GPS</span>
            <span className={styles.methodCardPill}>Mặc định</span>
          </div>
          <p className={styles.methodCardDescription}>Chấm công tại chi nhánh bằng vị trí GPS.</p>
        </button>
      )}

      <button
        type="button"
        className={`${styles.methodCard} ${styles.methodCardDisabled}`}
        onClick={() => setShowFaceIdLocked(true)}
        data-testid="method-card-faceid"
      >
        <div className={styles.methodCardHeader}>
          <span className={styles.methodCardTitle}>FaceID</span>
          <span className={`${styles.methodCardPill} ${styles.methodCardPillWarn}`}>🔒 Chờ xác nhận thư viện</span>
        </div>
        <p className={styles.methodCardDescription}>Chưa khả dụng — không mô phỏng hoạt động.</p>
      </button>

      {timemarkMethodEnabled && (
        <button
          type="button"
          className={`${styles.methodCard} ${selectedMethod === 'timemark' ? styles.methodCardSelected : ''}`}
          onClick={() => setSelectedMethod('timemark')}
          data-testid="method-card-timemark"
        >
          <div className={styles.methodCardHeader}>
            <span className={styles.methodCardTitle}>Timemark</span>
            <span className={`${styles.methodCardPill} ${styles.methodCardPillWarn}`}>Cần Quản lý duyệt</span>
          </div>
          <p className={styles.methodCardDescription}>Nhập lý do, gửi chờ duyệt.</p>
        </button>
      )}

      <div className={styles.actionsRow}>
        <button type="button" className={styles.secondaryButton} onClick={onCancel} data-testid="method-select-cancel">
          Huỷ
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => onContinue(selectedMethod)}
          data-testid="method-select-continue"
        >
          Tiếp tục
        </button>
      </div>

      {showFaceIdLocked && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowFaceIdLocked(false)}>
          <div className={styles.card} style={{ maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.headerContent}>
              <h2 className={styles.cardTitle}>FaceID</h2>
              <button type="button" className={styles.backButton} onClick={() => setShowFaceIdLocked(false)} data-testid="faceid-locked-close">
                ✕
              </button>
            </div>
            <p className={styles.cardDescription}>Chưa khả dụng — không mô phỏng hoạt động.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default MethodSelectScreen;
